import React from 'react';
import RichText from '../Global/RichText';
import CtaLink from '../Global/CtaLink';
import VideoPlayer from '../Global/VideoPlayer';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import {
    getColor,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
} from '../Global/DataUtils';
import type { NormalizedContentExplorerSlide } from './contentExplorerUtils';
import { plainTextFromHtml } from '../../utils/plainTextFromHtml';
import { isRichTextFieldBlank } from '../../global/utility';

type Styles = {
    readonly [key: string]: string;
};

function isValidVideoUrl(videoUrl: string | undefined): videoUrl is string {
    if (!videoUrl) return false;
    try {
        const parsed = new URL(videoUrl);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

function renderCtaForSlide(
    slide: NormalizedContentExplorerSlide,
    styles: Styles,
    onVideoOpenChange?: (open: boolean) => void
): React.ReactNode {
    const label = slide.ctaLabel;
    if (!label) return null;

    const ctaType = slide.ctaType || 'link';
    if (ctaType === 'video') {
        const videoUrl = slide.videoLink;
        if (!isValidVideoUrl(videoUrl)) return null;
        const playVideoAriaLabel =
            slide.playButtonAccessibilityLabel || `Play video: ${label}`;
        return (
            <div className={styles.ctaWrap}>
                <VideoPlayer
                    src={videoUrl}
                    ctaLabel={label}
                    ariaLabel={playVideoAriaLabel}
                    onOpenChange={onVideoOpenChange}
                />
            </div>
        );
    }

    const href = BuildLinkHref(slide.ctaLocation);
    if (!href) return null;
    let win = slide.ctaWindowAction;
    if (typeof win === 'string' && win.includes(' ')) {
        win = win.toLowerCase().split(' ').join('_') as typeof win;
    }
    return (
        <div className={styles.ctaWrap}>
            <CtaLink
                href={href}
                target={win === 'new_window' ? '_blank' : undefined}
                visualStyle="outlineLight"
                id={slide.ctaAnchorPoint}
            >
                {label}
            </CtaLink>
        </div>
    );
}

type ContentExplorerPanelProps = {
    styles: Styles;
    currentSlide: NormalizedContentExplorerSlide;
    activeIndex: number;
    panelId: string;
    panelBg: string;
    panelPrimary: string;
    panelSecondary: string;
    ariaLabelledBy: string;
    onVideoOpenChange?: (open: boolean) => void;
};

export function ContentExplorerPanel({
    styles,
    currentSlide,
    activeIndex,
    panelId,
    panelBg,
    panelPrimary,
    panelSecondary,
    ariaLabelledBy,
    onVideoOpenChange,
}: ContentExplorerPanelProps): JSX.Element {
    const rteKey = (field: string) =>
        `${currentSlide.id}-${activeIndex}-${field}`;

    return (
        <div
            className={styles.tabpanel}
            id={panelId}
            role="tabpanel"
            aria-labelledby={ariaLabelledBy}
            aria-live="polite"
            style={{
                backgroundColor: panelBg,
                color: panelPrimary,
            }}
        >
            <div className={styles.panelInner}>
                <div className={styles.panelBody}>
                    <div className={styles.panelLeft}>
                        {currentSlide?.categories?.length > 0 ||
                        (currentSlide?.contextLabel &&
                            !isRichTextFieldBlank(
                                currentSlide.contextLabel.value
                            )) ? (
                            <div className={styles.panelHeader}>
                                {currentSlide?.categories?.length > 0 ? (
                                    <div
                                        className={styles.badges}
                                        aria-label="Categories"
                                    >
                                        {currentSlide.categories.map(
                                            (cat, i) => {
                                                const color =
                                                    getColor(
                                                        cat.elements
                                                            ?.paragraph_copy_color
                                                    ) || '#fff';
                                                if (
                                                    !cat.elements?.content ||
                                                    isRichTextFieldBlank(
                                                        cat.elements.content
                                                            .value
                                                    )
                                                )
                                                    return null;
                                                return (
                                                    <div
                                                        key={`${currentSlide.id}-cat-${i}`}
                                                        className={styles.badge}
                                                        style={{
                                                            color,
                                                            borderColor: color,
                                                        }}
                                                    >
                                                        {RichText(
                                                            cat.elements
                                                                .content,
                                                            undefined,
                                                            undefined,
                                                            undefined,
                                                            'content'
                                                        )}
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                ) : null}
                                {currentSlide.contextLabel &&
                                !isRichTextFieldBlank(
                                    currentSlide.contextLabel.value
                                ) ? (
                                    <div
                                        key={rteKey('context')}
                                        className={styles.contextLabel}
                                    >
                                        {RichText(
                                            currentSlide.contextLabel,
                                            undefined,
                                            undefined,
                                            undefined,
                                            'contextLabel'
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <div className={styles.panelCopy}>
                            {currentSlide.headline &&
                            !isRichTextFieldBlank(
                                currentSlide.headline.value
                            ) ? (
                                <div
                                    key={rteKey('headline')}
                                    className={styles.slideHeadline}
                                    style={{ color: panelPrimary }}
                                >
                                    {RichText(
                                        currentSlide.headline,
                                        undefined,
                                        undefined,
                                        undefined,
                                        'headline'
                                    )}
                                </div>
                            ) : null}
                            {currentSlide.subHeadline &&
                            !isRichTextFieldBlank(
                                currentSlide.subHeadline.value
                            ) ? (
                                <div
                                    key={rteKey('subheadline')}
                                    className={styles.subHeadline}
                                    style={{ color: panelSecondary }}
                                >
                                    {RichText(
                                        currentSlide.subHeadline,
                                        undefined,
                                        undefined,
                                        undefined,
                                        'subHeadline'
                                    )}
                                </div>
                            ) : null}
                            {currentSlide.description &&
                            !isRichTextFieldBlank(
                                currentSlide.description.value
                            ) ? (
                                <div className={styles.bodyText}>
                                    {RichText(
                                        currentSlide.description,
                                        undefined,
                                        undefined,
                                        undefined,
                                        'description'
                                    )}
                                </div>
                            ) : null}
                            {currentSlide.highlightValue &&
                            !isRichTextFieldBlank(
                                currentSlide.highlightValue.value
                            ) ? (
                                <div
                                    className={styles.highlight}
                                    style={{ color: panelSecondary }}
                                >
                                    {RichText(
                                        currentSlide.highlightValue,
                                        undefined,
                                        undefined,
                                        undefined,
                                        'highlightValue'
                                    )}
                                </div>
                            ) : null}
                            {currentSlide.supportingContext &&
                            !isRichTextFieldBlank(
                                currentSlide.supportingContext.value
                            ) ? (
                                <div className={styles.supporting}>
                                    {RichText(
                                        currentSlide.supportingContext,
                                        undefined,
                                        undefined,
                                        undefined,
                                        'supportingContext'
                                    )}
                                </div>
                            ) : null}
                            {renderCtaForSlide(
                                currentSlide,
                                styles,
                                onVideoOpenChange
                            )}
                        </div>
                    </div>
                    {currentSlide.imageUrl ? (
                        <div className={styles.imageCol}>
                            <img
                                key={`${currentSlide.id}-${activeIndex}`}
                                className={styles.panelImage}
                                src={optimizeKontentImageUrl(
                                    currentSlide.imageUrl,
                                    { ...imagePreset.halfWidth.desktop, q: 95 }
                                )}
                                srcSet={buildKontentSrcSet(
                                    currentSlide.imageUrl,
                                    imagePreset.halfWidth.desktop.w,
                                    { fm: 'webp' as const, q: 95 }
                                )}
                                sizes="(max-width: 768px) 100vw, 50vw"
                                alt={
                                    currentSlide.imageAlt ||
                                    plainTextFromHtml(
                                        currentSlide.headline?.value
                                    ) ||
                                    'Program'
                                }
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
